namespace $ {

	export type $rise_rpc_ref = string

	export type $rise_rpc_call = {
		kind: 'call'
		id: number
		target: $rise_rpc_ref
		path: readonly string[]
		args: readonly $rise_rpc_data[]
		watch?: string
	}

	export type $rise_rpc_done = {
		kind: 'done'
		id: number
		value: $rise_rpc_data
	}

	export type $rise_rpc_fail = {
		kind: 'fail'
		id: number
		error: {
			name?: string
			message: string
			stack?: string
			cause?: $rise_rpc_data
		}
	}

	export type $rise_rpc_release = {
		kind: 'release'
		refs: readonly $rise_rpc_ref[]
	}

	export type $rise_rpc_stale = {
		kind: 'stale'
		watch: string
	}

	export type $rise_rpc_unwatch = {
		kind: 'unwatch'
		watches: readonly string[]
	}

	export type $rise_rpc_message =
		| $rise_rpc_call
		| $rise_rpc_done
		| $rise_rpc_fail
		| $rise_rpc_release
		| $rise_rpc_stale
		| $rise_rpc_unwatch

	export type $rise_rpc_data =
		| null
		| boolean
		| number
		| string
		| readonly $rise_rpc_data[]
		| { readonly [ key: string ]: $rise_rpc_data }

	type $rise_rpc_proxy_meta = {
		ref: $rise_rpc_ref
		path: readonly string[]
	}

	export class $rise_rpc extends $mol_object {

		protected readonly root_ref = 'root'
		protected next_id = -1
		protected next_ref = 1
		protected readonly asks = new Map< number, {
			done: ( value: unknown )=> void
			fail: ( error: Error )=> void
			timer?: ReturnType< typeof setTimeout >
		} >()
		protected replays = new WeakMap< object, Map< string, {
			promise: Promise< unknown >
			done: boolean
			pub: $mol_wire_pub
			watch: string
			value?: unknown
			error?: Error
		} > >()
		protected next_watch = 1
		protected readonly watches = new Map< string, {
			key: string
			pub: $mol_wire_pub
			replays: Map< string, {
				promise: Promise< unknown >
				done: boolean
				pub: $mol_wire_pub
				watch: string
				value?: unknown
				error?: Error
			} >
		} >()
		protected readonly watch_calls = new Map< string, $rise_rpc_call >()
		protected readonly watch_ready = new Set< string >()
		protected readonly watch_atoms = new Map< string, $mol_wire_atom< any, [ string ], unknown > >()
		protected readonly watch_subs = new Map< string, $mol_wire_pub_sub >()
		protected readonly watch_plans = new Set< string >()
		protected readonly refs = new Map< $rise_rpc_ref, unknown >()
		protected reverse_refs = new WeakMap< object, $rise_rpc_ref >()
		protected readonly proxies = new WeakMap< object, $rise_rpc_proxy_meta >()
		protected readonly receives = new Set< Promise< unknown > >()
		protected readonly finalizer = typeof FinalizationRegistry === 'undefined'
			? null
			: new FinalizationRegistry< $rise_rpc_proxy_meta >( meta => this.release_meta( meta ) )

		root(): unknown {
			return this.$
		}

		target() {
			return {
				send: ( message: $rise_rpc_message )=> {},
			}
		}

		remote< Remote extends object >() {
			return this.proxy< Remote >({
				ref: this.root_ref,
				path: [],
			})
		}

		remote_at< Remote extends object >( path: readonly string[] ) {
			return this.proxy< Remote >({
				ref: this.root_ref,
				path,
			})
		}

		api< Api extends object >() {
			return this.remote_at< Api >([ 'server', 'api' ])
		}

		protected proxy< Remote extends object >( meta: $rise_rpc_proxy_meta ) {

			const rpc = this

			const remote = new Proxy( function() {} as any, {

				get( target, field: string | symbol ) {

					if( field === Symbol.toStringTag ) return '$rise_rpc_remote'
					if( field === Symbol.toPrimitive ) return ()=> `[${ meta.ref }:${ meta.path.join( '.' ) }]`
					if( field === 'toString' ) return ()=> `[${ meta.ref }:${ meta.path.join( '.' ) }]`
					if( field === 'destructor' ) return ()=> rpc.release_meta( meta )
					if( field === 'then' ) return undefined
					if( typeof field !== 'string' ) return undefined

					return rpc.proxy({
						ref: meta.ref,
						path: [ ... meta.path, field ],
					})

				},

				apply( target, self, args: readonly unknown[] ) {
					return rpc.call( meta.ref, meta.path, args )
				},

			} )

			this.proxies.set( remote, meta )
			if( meta.ref !== this.root_ref && meta.path.length === 0 ) this.finalizer?.register( remote, meta )

			return remote as Remote
		}

		release( value: unknown ) {
			const meta = this.proxy_meta( value )
			if( !meta ) return false
			return this.release_meta( meta )
		}

		protected release_meta( meta: $rise_rpc_proxy_meta ) {
			if( meta.ref === this.root_ref ) return false
			if( meta.path.length !== 0 ) return false
			
			try {
				this.target().send({
					kind: 'release',
					refs: [ meta.ref ],
				})
			} catch( error ) {
				$mol_fail_log( error )
				return false
			}
			
			return true
		}

		protected call( target: $rise_rpc_ref, path: readonly string[], args: readonly unknown[] ) {

			const packed = args.map( arg => this.pack( arg ) )
			const key = JSON.stringify([ target, path, packed ])
			const sub = $mol_wire_auto()

			if( !sub ) return this.call_packed_async( target, path, packed )
			let replays = this.replays.get( sub )
			if( !replays ) {
				replays = new Map
				this.replays.set( sub, replays )
			}

			const replay = replays.get( key )
			if( replay ) {
				replay.pub.promote()
				if( !replay.done ) throw replay.promise
				if( replay.error ) throw replay.error
				return replay.value
			}

			const watch = `watch${ this.next_watch++ }`
			const pub = new $mol_wire_pub( `$rise_rpc_watch:${ watch }` )
			pub.reap = ()=> {
				Promise.resolve().then( ()=> {
					if( pub.sub_empty ) this.unwatch( watch )
				} )
			}
			pub.promote()

			const next = {
				promise: null as any as Promise< unknown >,
				done: false,
				pub,
				watch,
				value: undefined as unknown,
				error: undefined as Error | undefined,
			}

			this.watches.set( watch, { key, pub, replays } )

			next.promise = this.call_packed_async( target, path, packed, watch ).then(
				value => {
					next.done = true
					next.value = value
					return value
				},
				error => {
					next.done = true
					next.error = error instanceof Error ? error : new Error( String( error ), { cause: error } )
					throw next.error
				},
			)

			replays.set( key, next )
			throw next.promise
		}

		protected unwatch( watch: string ) {
			const watched = this.watches.get( watch )
			if( !watched ) return false

			this.watches.delete( watch )
			watched.replays.delete( watched.key )

			try {
				this.target().send({
					kind: 'unwatch',
					watches: [ watch ],
				})
			} catch( error ) {
				$mol_fail_log( error )
			}

			return true
		}

		protected transport_touch() {}

		call_timeout() {
			return 30_000
		}

		call_async( target: $rise_rpc_ref, path: readonly string[], args: readonly unknown[] ) {
			return this.call_packed_async(
				target,
				path,
				args.map( arg => this.pack( arg ) ),
			)
		}

		protected call_packed_async( target: $rise_rpc_ref, path: readonly string[], args: readonly $rise_rpc_data[], watch?: string ) {

			const id = this.next_id--

			const message: $rise_rpc_call = {
				kind: 'call',
				id,
				target,
				path,
				args,
				watch,
			}

			return new Promise< unknown >( ( done, fail )=> {
				const ask = { done, fail } as {
					done: ( value: unknown )=> void
					fail: ( error: Error )=> void
					timer?: ReturnType< typeof setTimeout >
				}
				const timeout = this.call_timeout()

				if( Number.isFinite( timeout ) && timeout >= 0 ) {
					ask.timer = setTimeout(
						()=> this.ask_fail( id, new Error( `RPC call timeout: ${ message.target }.${ message.path.join( '.' ) }` ) ),
						timeout,
					)
				}

				this.asks.set( id, ask )

				Promise.resolve().then( ()=> {
					if( !this.asks.has( id ) ) return
					return this.target().send( message )
				} ).catch( error => {
					this.ask_fail( id, error instanceof Error ? error : new Error( String( error ), { cause: error } ) )
				} )
			} )
		}

		abort( error: Error ) {
			for( const id of [ ... this.asks.keys() ] ) this.ask_fail( id, error )
			this.refs.clear()
			this.reverse_refs = new WeakMap
			this.replays = new WeakMap
			this.watches.clear()
			this.watch_calls.clear()
			this.watch_ready.clear()
			this.watch_plans.clear()
			for( const sub of this.watch_subs.values() ) sub.destructor()
			this.watch_subs.clear()
			for( const atom of this.watch_atoms.values() ) atom.destructor()
			this.watch_atoms.clear()
			this.receives.clear()
		}

		protected ask_done( id: number, value: unknown ) {
			const ask = this.asks.get( id )
			if( !ask ) return
			this.asks.delete( id )
			if( ask.timer ) clearTimeout( ask.timer )
			ask.done( value )
		}

		protected ask_fail( id: number, error: Error ) {
			const ask = this.asks.get( id )
			if( !ask ) return
			this.asks.delete( id )
			if( ask.timer ) clearTimeout( ask.timer )
			ask.fail( error )
		}

		receive_async( message: $rise_rpc_message ) {
			if( message.kind !== 'call' ) {
				this.receive( message )
				return Promise.resolve()
			}

			const promise = this.receive_call_async( message )
			this.receives.add( promise )
			promise.finally( ()=> this.receives.delete( promise ) )
			return promise
		}

		receive( message: $rise_rpc_message ) {

			switch( message.kind ) {

				case 'call':
					return this.receive_call_async( message )

				case 'done': {
					this.ask_done( message.id, this.unpack( message.value ) )
					return
				}

				case 'fail': {
					const error = new Error( message.error.message )
					error.name = message.error.name ?? error.name
					error.stack = message.error.stack
					;( error as Error & { cause?: unknown } ).cause = this.unpack( message.error.cause ?? null )
					this.ask_fail( message.id, error )
					return
				}

				case 'release': {
					for( const ref of message.refs ) this.ref_release( ref )
					return
				}

				case 'stale': {
					const watched = this.watches.get( message.watch )
					if( !watched ) return
					watched.replays.delete( watched.key )
					this.watches.delete( message.watch )
					watched.pub.emit()
					return
				}

				case 'unwatch': {
					for( const watch of message.watches ) this.watch_drop( watch )
					return
				}

			}

		}

		protected async receive_call_async( message: $rise_rpc_call ) {

			try {

				const value = message.watch
					? await this.watch_async( message.watch, message )
					: await this.exec_async( message )

				this.target().send({
					kind: 'done',
					id: message.id,
					value: this.pack( value ),
				})

			} catch( error: any ) {

				if( $mol_promise_like( error ) ) $mol_fail_hidden( error )

				this.target().send({
					kind: 'fail',
					id: message.id,
					error: {
						name: error?.name,
						message: error?.message ?? String( error ),
						stack: error?.stack,
						cause: this.pack( error?.cause ?? null ),
					},
				})

			}

		}

		protected async exec_async( message: $rise_rpc_call ) {

			const task = $mol_wire_task.getter( ()=> this.exec( message ) )
			const fiber = task( this, [] )
			return fiber.async()

		}

		protected async watch_async( watch: string, message: $rise_rpc_call ) {

			this.watch_calls.set( watch, message )

			const atom = this.watch_atom( watch )
			const value = await atom.async()
			this.watch_keep( watch, atom )
			return value

		}

		protected watch_atom( watch: string ) {

			let atom = this.watch_atoms.get( watch )
			if( atom ) return atom

			const task = ( this.watch_value as any ).orig as ( this: this, watch: string )=> unknown
			atom = $mol_wire_atom.plex( this, task, watch )
			atom.reap = ()=> {}
			this.watch_atoms.set( watch, atom )

			return atom
		}

		protected watch_keep( watch: string, atom: $mol_wire_atom< any, [ string ], unknown > ) {

			if( this.watch_subs.has( watch ) ) return

			const sub = new $mol_wire_pub_sub( `$rise_rpc_watch:${ watch }` )
			sub.absorb = ()=> {
				if( !this.watch_atoms.has( watch ) ) return
				if( this.watch_plans.has( watch ) ) return
				this.watch_plans.add( watch )
				Promise.resolve().then( ()=> {
					this.watch_plans.delete( watch )
					if( !this.watch_atoms.has( watch ) ) return
					atom.fresh()
				} )
			}

			const prev = sub.track_on()
			sub.track_next( atom )
			sub.track_off( prev )

			this.watch_subs.set( watch, sub )

		}

		@ $mol_mem_key
		protected watch_value( watch: string ) {

			const message = this.watch_calls.get( watch )
			if( !message ) return null

			const value = this.exec( message )

			if( this.watch_ready.has( watch ) ) {
				this.target().send({
					kind: 'stale',
					watch,
				})
				Promise.resolve().then( ()=> this.watch_drop( watch ) )
			} else {
				this.watch_ready.add( watch )
			}

			return value
		}

		protected watch_drop( watch: string ) {

			this.watch_calls.delete( watch )
			this.watch_ready.delete( watch )
			this.watch_plans.delete( watch )

			const sub = this.watch_subs.get( watch )
			this.watch_subs.delete( watch )
			sub?.destructor()

			const atom = this.watch_atoms.get( watch )
			this.watch_atoms.delete( watch )
			atom?.destructor()

		}

		exec( message: $rise_rpc_call ) {

			let host = this.ref( message.target )
			const args = message.args.map( arg => this.unpack( arg ) )

			if( message.path.length === 0 ) {
				if( typeof host !== 'function' ) return host
				return host( ... args )
			}

			for( const field of message.path.slice( 0, -1 ) ) {
				host = ( host as any )[ field ]
			}

			const field = message.path[ message.path.length - 1 ]
			const value = ( host as any )[ field ]

			if( typeof value === 'function' ) return value.apply( host, args )
			if( args.length === 0 ) return value
			if( args.length === 1 ) return ( host as any )[ field ] = args[0]

			return $mol_fail( new Error( `Not a function: ${ message.path.join( '.' ) }` ) )
		}

		protected ref( id: $rise_rpc_ref ) {
			if( id === this.root_ref ) return this.root()
			if( this.refs.has( id ) ) return this.refs.get( id )
			return $mol_fail( new Error( `Unknown remote ref: ${ id }` ) )
		}

		protected pack( value: unknown ): $rise_rpc_data {

			const meta = this.proxy_meta( value )
			if( meta && meta.path.length === 0 ) return { $rise_rpc_ref: meta.ref }

			if( value === undefined ) return { $rise_rpc_undefined: true }
			if( value === null ) return null

			switch( typeof value ) {
				case 'boolean':
				case 'string':
					return value
				case 'number':
					if( Number.isFinite( value ) ) return value
					return { $rise_rpc_number: String( value ) }
				case 'bigint':
					return { $rise_rpc_bigint: String( value ) }
				case 'object':
				case 'function':
					break
				default:
					return $mol_fail( new Error( `Unsupported RPC value: ${ typeof value }` ) )
			}

			if( Array.isArray( value ) ) return value.map( item => this.pack( item ) )

			if( this.plain( value ) && this.plain_data( value ) ) {
				const keys = Object.keys( value )
				if( keys.some( key => this.reserved_key( key ) ) ) {
					return {
						$rise_rpc_plain: keys.map( key => [
							key,
							this.pack( ( value as any )[ key ] ),
						] ),
					}
				}
				
				const next = {} as Record< string, $rise_rpc_data >
				for( const key of keys ) next[ key ] = this.pack( ( value as any )[ key ] )
				return next
			}

			return { $rise_rpc_ref: this.ref_store( value ) }
		}

		protected unpack( value: $rise_rpc_data ): unknown {

			if( value === null ) return null

			if( Array.isArray( value ) ) return value.map( item => this.unpack( item ) )

			if( typeof value !== 'object' ) return value

			const keys = Object.keys( value )
			
			if( keys.length === 1 ) {
				if( '$rise_rpc_ref' in value ) return this.proxy({
					ref: value.$rise_rpc_ref as string,
					path: [],
				})
	
				if( '$rise_rpc_undefined' in value ) return undefined
				if( '$rise_rpc_bigint' in value ) return BigInt( value.$rise_rpc_bigint as string )
				if( '$rise_rpc_number' in value ) return Number( value.$rise_rpc_number )
				if( '$rise_rpc_plain' in value ) {
					const next = {} as Record< string, unknown >
					for( const entry of value.$rise_rpc_plain as readonly ( readonly [ string, $rise_rpc_data ] )[] ) {
						next[ entry[0] ] = this.unpack( entry[1] )
					}
					return next
				}
			}

			const data = value as { readonly [ key: string ]: $rise_rpc_data }
			const next = {} as Record< string, unknown >
			for( const key of keys ) next[ key ] = this.unpack( data[ key ] )
			return next
		}

		protected reserved_key( key: string ) {
			switch( key ) {
				case '$rise_rpc_ref':
				case '$rise_rpc_undefined':
				case '$rise_rpc_bigint':
				case '$rise_rpc_number':
				case '$rise_rpc_plain':
					return true
				default:
					return false
			}
		}

		protected proxy_meta( value: unknown ) {
			if( typeof value !== 'object' && typeof value !== 'function' ) return null
			if( value === null ) return null
			return this.proxies.get( value )
		}

		protected plain( value: object ) {
			const proto = Object.getPrototypeOf( value )
			return proto === Object.prototype || proto === null
		}

		protected plain_data( value: unknown, seen = new WeakSet< object >() ): boolean {

			if( value === null ) return true

			switch( typeof value ) {
				case 'boolean':
				case 'number':
				case 'string':
				case 'undefined':
				case 'bigint':
					return true
				case 'object':
					break
				default:
					return false
			}

			if( seen.has( value ) ) return false
			seen.add( value )

			if( Array.isArray( value ) ) return value.every( item => this.plain_data( item, seen ) )
			if( !this.plain( value ) ) return false

			return Object.keys( value ).every( key => this.plain_data( ( value as any )[ key ], seen ) )
		}

		protected ref_store( value: object ) {

			const prev = this.reverse_refs.get( value )
			if( prev ) return prev

			const id = `ref${ this.next_ref++ }`
			this.reverse_refs.set( value, id )
			this.refs.set( id, value )

			return id
		}

		protected ref_release( id: $rise_rpc_ref ) {
			if( id === this.root_ref ) return false
			const value = this.refs.get( id )
			const released = this.refs.delete( id )
			if( value && ( typeof value === 'object' || typeof value === 'function' ) ) {
				this.reverse_refs.delete( value )
			}
			return released
		}

	}

}
